import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { debounce } from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';
import { TransferProduksiAlatMesinApi } from 'api';
import {
  Alert,
  DataStatus,
  InputSearch,
  Pagination,
  Td,
  Th,
  ThFixed,
  Table,
  FilterButton,
  CreateButton,
} from 'components';
import { DateConvert, PageNumber as TableNumber } from 'utilities';
import { useSessionStorage } from 'hooks';
import { ModalFilterTransferAlat } from './Components';
import { withDropdownTransferAlatProvider } from './Context';

const TransferProduksiAlatMesin = ({ setNavbarTitle }) => {
  const title = 'Transfer Alat Mesin Produksi';
  const history = useHistory();
  const location = useLocation();
  const PAGINATION_KEY = `${location.pathname}_pagination`;

  const [modalFilter, setModalFilter] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useSessionStorage(PAGINATION_KEY, {
    q: '',
    page: '1',
    per_page: '10',
    data_count: '0',
    total_page: '1',

    active: false,
    tgl_job_order_start: undefined,
    tgl_job_order_end: undefined,
    tgl_transfer_produksi_alat_mesin_start: undefined,
    tgl_transfer_produksi_alat_mesin_end: undefined,
    id_barang_jadi: undefined,
  });
  const [alertConfig, setAlertConfig] = useState({
    variant: 'primary',
    text: '',
  });

  const getInitialData = () => {
    setIsLoading(true);

    TransferProduksiAlatMesinApi.page({
      page: pagination.page,
      per_page: pagination.per_page,
      q: pagination.q,

      tgl_job_order_start: pagination.tgl_job_order_start,
      tgl_job_order_end: pagination.tgl_job_order_end,
      tgl_transfer_produksi_alat_mesin_start:
        pagination.tgl_transfer_produksi_alat_mesin_start,
      tgl_transfer_produksi_alat_mesin_end:
        pagination.tgl_transfer_produksi_alat_mesin_end,
      id_barang_jadi: pagination.id_barang_jadi,
    })
      .then((res) => {
        setData(res.data.data);
        setPagination((prev) => ({
          ...prev,
          data_count: res.data.data_count,
          total_page: res.data.total_page,
        }));
      })
      .catch(() => {
        setAlertConfig({
          variant: 'danger',
          text: 'Data gagal dimuat',
        });
      })
      .finally(() => setIsLoading(false));
  };

  const checkAlert = () => {
    const locationState = location.state;

    if (locationState?.alert) {
      setAlertConfig(locationState.alert);
    }
  };

  const dismissAlert = () => {
    const currentState = history.location.state;

    if (currentState?.alert) {
      let state = { ...currentState };
      delete state.alert;

      history.replace(history.location.pathname, state);
      setAlertConfig({
        text: '',
        variant: 'primary',
      });
    }
  };

  useEffect(() => {
    setNavbarTitle(title);
    getInitialData();
  }, [
    setNavbarTitle,
    pagination.page,
    pagination.per_page,
    pagination.q,

    pagination.tgl_job_order_start,
    pagination.tgl_job_order_end,
    pagination.tgl_transfer_produksi_overhead_start,
    pagination.tgl_transfer_produksi_overhead__end,
    pagination.id_barang_jadi,
  ]);

  useEffect(() => checkAlert(), []);

  const TableContent = () => {
    return (
      <>
        <div className="font-weight-bold mt-2" style={{ fontSize: '.9rem' }}>
          List Data Transfer Alat Mesin Produksi
        </div>

        <Table>
          <thead>
            <tr className="text-center">
              <ThFixed>No</ThFixed>
              <ThFixed>Informasi Job Order</ThFixed>
              <Th width={250}>Item Job Order</Th>
              <ThFixed>Informasi Transfer Alat Mesin Produksi</ThFixed>
              <Th width={250}>Item Alat Mesin</Th>
              <Th width={150}>Diserahkan Kepada</Th>
              <Th width={200}>Keterangan</Th>
            </tr>
          </thead>

          <tbody>
            {data.map((val, index) => (
              <tr key={index}>
                <Td className="text-center align-middle">
                  {TableNumber(pagination.page, pagination.per_page, index)}
                </Td>

                <Td className="align-middle">
                  <div>
                    {val.tgl_job_order
                      ? DateConvert(new Date(val.tgl_job_order)).detail
                      : '-'}
                  </div>
                  <div>{val.no_job_order}</div>
                </Td>

                <Td className="align-middle">{val.nama_barang_jadi}</Td>

                <Td className="align-middle">
                  <div>
                    {val.tgl_transfer_produksi_alat_mesin
                      ? DateConvert(
                          new Date(val.tgl_transfer_produksi_alat_mesin)
                        ).detail
                      : '-'}
                  </div>
                  <div>{val.no_transfer_produksi_alat_mesin}</div>
                </Td>

                <Td className="align-middle">
                  {`
                    ${val.nama_item_aset} 
                    ${parseFloat(val.qty_transfer_produksi_alat_mesin ?? '0')} 
                    ${val.nama_satuan_pakai ?? ''}
                  `}
                </Td>

                <Td className="align-middle">{val.nama_karyawan}</Td>

                <Td className="align-middle">{val.keterangan}</Td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Pagination
          dataLength={pagination.per_page}
          dataCount={pagination.data_count}
          currentPage={pagination.page}
          totalPage={pagination.total_page}
          dataNumber={
            pagination.page * pagination.per_page - pagination.per_page + 1
          }
          onPaginationChange={({ selected }) =>
            setPagination((prev) => ({ ...prev, page: selected + 1 }))
          }
          onDataLengthChange={(e) =>
            setPagination((prev) => ({
              ...prev,
              page: 1,
              per_page: e.target.value,
            }))
          }
        />
      </>
    );
  };

  return (
    <>
      <Row className="d-flex align-items-center mb-2">
        <Col md="10" className="d-flex">
          <InputSearch
            onChange={debounce((e) => {
              const searchKey = e.target.value;

              setPagination((prev) => ({
                ...prev,
                q: searchKey,
                page: '1',
              }));
            }, 1500)}
          />

          <FilterButton
            active={pagination.active}
            onClick={() => setModalFilter(true)}
          />
        </Col>

        <Col className="d-flex justify-content-end align-items-end">
          <CreateButton
            onClick={() =>
              history.push(
                '/transaksi/transfer-produksi-alat-mesin/list-permintaan-produksi'
              )
            }
          />
        </Col>
      </Row>

      <Alert
        show={Boolean(alertConfig?.text?.length)}
        showCloseButton={true}
        variant={alertConfig.variant}
        text={alertConfig.text}
        onClose={dismissAlert}
      />

      {isLoading === true ? (
        <DataStatus loading={true} text="Memuat data..." />
      ) : data.length > 0 ? (
        <TableContent />
      ) : (
        <DataStatus text="Tidak ada data" />
      )}

      <ModalFilterTransferAlat
        show={modalFilter}
        setShow={setModalFilter}
        filter={pagination}
        setFilter={setPagination}
      />
    </>
  );
};

export default withDropdownTransferAlatProvider(TransferProduksiAlatMesin);
